// Shared helpers for the organiser event pages (src/page/brand/events/*).

export const STATUS_LABELS = {
    draft: 'Draft',
    rejected: 'Changes requested',
    in_review: 'In review',
    approved: 'Approved',
    live: 'Live',
    ended: 'Ended',
}

// My Events tab each status lives under (mirrors TAB_STATUSES in the API).
export const STATUS_TAB = {
    draft: 'draft',
    rejected: 'draft',
    in_review: 'review',
    approved: 'review',
    live: 'live',
    ended: 'archive',
}

export function browserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch (e) {
        return 'UTC'
    }
}

// IANA zones offered in the Basics timezone picker. Falls back to a short list on
// browsers without Intl.supportedValuesOf.
export function timezoneOptions() {
    let zones = []
    try {
        zones = Intl.supportedValuesOf('timeZone')
    } catch (e) {
        zones = ['Asia/Singapore', 'Australia/Melbourne', 'Australia/Sydney', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles', 'UTC']
    }
    const current = browserTimezone()
    if (!zones.includes(current)) zones.unshift(current)
    return zones
}

// Offset (ms) of `timeZone` from UTC at the given instant.
function zoneOffset(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(date)
    const get = (type) => Number(parts.find((p) => p.type === type).value)
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
    return asUtc - Math.floor(date.getTime() / 1000) * 1000
}

// The date pickers work in "wall-clock" values: a JS Date whose local fields
// show what the organiser typed for the event's zone. These convert between
// that and the real UTC instant the API stores.
export function wallClockToUtc(wall, timeZone) {
    if (!wall) return null
    const tz = timeZone || browserTimezone()
    const naiveUtc = Date.UTC(wall.getFullYear(), wall.getMonth(), wall.getDate(), wall.getHours(), wall.getMinutes(), 0)
    const guess = new Date(naiveUtc - zoneOffset(new Date(naiveUtc), tz))
    // Re-check the offset at the guessed instant so DST boundaries land right.
    return new Date(naiveUtc - zoneOffset(guess, tz))
}

export function utcToWallClock(iso, timeZone) {
    if (!iso) return null
    const tz = timeZone || browserTimezone()
    const instant = new Date(iso)
    const shifted = new Date(instant.getTime() + zoneOffset(instant, tz))
    return new Date(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), shifted.getUTCHours(), shifted.getUTCMinutes())
}

// "Sat, 19 Dec 2026" or "Fri, 12 Jun – Sat, 13 Jun 2026", shown in the event's zone.
export function formatEventDates(start, end, timeZone) {
    if (!start) return 'Date to be announced'
    const fmt = (date, withYear) => {
        const parts = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: timeZone || undefined }).formatToParts(new Date(date))
        const get = (type) => parts.find((p) => p.type === type).value
        return `${get('weekday')}, ${get('day')} ${get('month')}${withYear ? ` ${get('year')}` : ''}`
    }
    const first = fmt(start, true)
    if (!end || fmt(end, true) === first) return first
    return `${fmt(start, false)} – ${fmt(end, true)}`
}

export function daysToGo(start) {
    if (!start) return null
    const days = Math.ceil((new Date(start).getTime() - Date.now()) / 86400000)
    return days > 0 ? days : 0
}

// API errors come back as { code, msg } (HttpExceptionFilter); fall back to axios text.
export function apiErrorMessage(err, fallback = 'Something went wrong') {
    const body = err && err.response && err.response.data
    if (body && body.msg) return Array.isArray(body.msg) ? body.msg.join(', ') : body.msg
    if (body && body.message) return Array.isArray(body.message) ? body.message.join(', ') : body.message
    return fallback
}

// "Create ▾ → New event": start a server-side draft and open its Basics step.
export async function startNewEvent(vm) {
    const organizerApi = require('@/api/organizer').default
    try {
        const res = await organizerApi.createEvent()
        vm.$router.push({ path: '/brand/events/edit', query: { id: res.data.id, step: 'basics' } })
    } catch (err) {
        vm.pushMsg('error', apiErrorMessage(err, vm.langJson('Could not create the event')))
    }
}

// vue-router 3.0 returns undefined from push/replace (no promise to .catch), and
// newer 3.x rejects on a duplicate navigation — handle both.
export function navigate(vm, location, { replace = false } = {}) {
    const result = vm.$router[replace ? 'replace' : 'push'](location)
    if (result && typeof result.catch === 'function') result.catch(() => {})
}
